import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/auth/consoleCapabilityContext';
import { Button, Input, PageHeader } from '@/components/ui';
import { EffectiveConfigPanel } from './config/EffectiveConfigPanel';

/**
 * EAA-1776 / EAA-1823: `/console/config` — Cấu hình. Built to design frame 64
 * (`Config BE runtime`, Portal 7), which supersedes frame 54's read-only drawing.
 *
 * Shows the configuration the service is actually running with
 * (`GET /admin/config`), and edits the keys the server declares editable through
 * a two-step confirmation.
 *
 * EAA-1823 also DELETED the "Cấu hình khởi động" block this screen used to be
 * made of — a read-only dump of what this browser had loaded from
 * `/config.json`. Every part of it was a worse copy of something already on
 * screen or one tab away, and the boot copy is the stale one by construction
 * since it is frozen at page load. Boot config is still observable at
 * `/config.json` itself.
 *
 * The filter lives HERE rather than in the panel because frame 64 puts it in the
 * page header beside the title, and `PageHeader` owns that row.
 *
 * Gating (§1b): `console.config.view` gates the screen; edit affordances
 * additionally require `console.config.manage`, and the server re-checks both.
 */
export function ConfigPage() {
    const can = useCan();
    const { t } = useTranslation();
    const [filter, setFilter] = useState('');

    if (!can('console.config.view')) {
        return null;
    }

    return (
        <main className="portal-admin portal-console-page">
            <PageHeader
                title={t('console.config.title')}
                subtitle={t('console.config.effectiveHint')}
                // Frame 64 prints this subtitle under the title rather than
                // collapsing it behind the (i) the console defaults to. It earns
                // the space here: it is what tells an operator that editing a row
                // changes the running system immediately, which is the one thing
                // they must know before touching this screen.
                collapseSubtitle={false}
                actions={
                    <div className="portal-console-config-header-actions">
                        <Input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder={t('console.config.filterPlaceholder')}
                            allowClear
                        />
                        {/* Every stage and confirm already writes an AdminAudit row;
                            this goes to them rather than duplicating a history UI. */}
                        <Link to="/console/audit?source=admin">
                            <Button>{t('console.config.changeLogAction')}</Button>
                        </Link>
                    </div>
                }
            />

            <EffectiveConfigPanel filter={filter} onClearFilter={() => setFilter('')} />
        </main>
    );
}
